import axios from "axios";
import Swal from "sweetalert2";

export const createReviewPeriod = async (periodData) => {
  try {
    const response = await axios.post(`/appraisal/review-periods`, periodData);
    return response.data.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to create review period"
    );
  }
};

export const getAllReviewPeriods = async () => {
  try {
    const response = await axios.get(`/appraisal/review-periods`);
    return response.data.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch review periods"
    );
  }
};

export const updateReviewPeriod = async (id, periodData) => {
  try {
    const response = await axios.put(
      `/appraisal/review-periods/${id}`,
      periodData
    );
    return response.data.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to update review period"
    );
  }
};

export const deleteReviewPeriod = async (id) => {
  try {
    const response = await axios.delete(`/appraisal/review-periods/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to delete review period"
    );
  }
};

export const getUniqueAppraisalsByReviewPeriod = async (
  periodId,
  params = {}
) => {
  try {
    const response = await axios.get(`/appraisal/period/${periodId}`, {
      params,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error fetching unique appraisals:", error);
    throw error.response?.data?.message || "Failed to fetch unique appraisals";
  }
};

export const assignAppraisal = async (kpiData) => {
  try {
    console.log("[DEBUG] Sending KPI data:", JSON.stringify(kpiData, null, 2));

    const response = await axios.post(`/appraisal/assign-kpis`, kpiData, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    console.log("[DEBUG] API Response:", response.data);
    return response.data.data;
  } catch (error) {
    console.error("[ERROR] Failed to assign KPIs:", {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
    });
    throw error;
  }
};
export const updateAppraisal = async (id, appraisalData) => {
  try {
    const response = await axios.put(
      `/appraisal/appraisals/${id}`,
      appraisalData
    );
    return response.data.data;
  } catch (error) {
    console.error(
      "Error updating appraisal:",
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message || "Failed to update appraisal"
    );
  }
};

export const deleteAppraisal = async (id) => {
  try {
    await axios.delete(`/appraisal/appraisals/${id}`);
    return true;
  } catch (error) {
    console.error(
      "Error deleting appraisal:",
      error.response?.data || error.message
    );
    throw new Error(
      error.response?.data?.message || "Failed to delete appraisal"
    );
  }
};

export const getAppraisalList = async () => {
  try {
    const response = await axios.get(`/appraisal`);
    return response.data.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch appraisals"
    );
  }
};

export const getAllAppraisalsByUserId = async (userId) => {
  try {
    const response = await axios.get(`/appraisal/my-appraisals/${userId}`, {
      headers: {
        "Content-Type": "application/json",
        // Add Authorization header if needed
        // 'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
    });
    return response.data.data; // Return the appraisals array
  } catch (error) {
    console.error("Error fetching appraisals:", error);
    return [];
  }
};

export const updateAppraisalAchievements = async (
  userId,
  appraisalId,
  kpis
) => {
  try {
    const response = await axios.put(
      `/appraisal/update-achievements/${userId}/${appraisalId}`,
      { kpis },
      {
        headers: {
          "Content-Type": "application/json",
          // Add Authorization header if needed
          // 'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
      }
    );
    return response.data.data; // Return the updated appraisal
  } catch (error) {
    console.error("Error updating achievements:", error);
    throw error.response?.data || { message: "Failed to update achievements" };
  }
};

export const searchAppraisals = async ({
  userId,
  periodName,
  departmentName,
  appraisalName,
  status,
  setLoading,
}) => {
  try {
    setLoading(true);
    const response = await axios.get(`/appraisal/my-appraisals/${userId}`, {
      params: {
        periodName,
        departmentName,
        appraisalName,
        status,
      },
      headers: {
        "Content-Type": "application/json",
        // Add Authorization header if needed
        // 'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
    });
    setLoading(false);
    return response.data.data;
  } catch (error) {
    console.error("Error searching appraisals:", error);
    setLoading(false);
    return [];
  }
};

export const getTeamAppraisals = async (userId) => {
  try {
    const response = await axios.get(`/appraisal/team-appraisals/${userId}`);
    return response.data.data || [];
  } catch (error) {
    console.error("Error fetching team appraisals:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data?.message || "Failed to fetch team appraisals.",
    });
    return null;
  }
};

export const reviewAppraisal = async (userId, appraisalId, data) => {
  try {
    const response = await axios.put(`/appraisal/review/${userId}`, {
      appraisalId,
      ...data,
    });
    return response.data.data;
  } catch (error) {
    console.error("Error reviewing appraisal:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data?.message || "Failed to review appraisal.",
    });
    throw error;
  }
};

export const searchTeamAppraisals = async ({
  userId,
  appraisalName,
  periodName,
  employeeName,
  status,
  setLoading,
}) => {
  try {
    setLoading(true);
    const response = await axios.get(`/appraisal/team-appraisals/${userId}`, {
      params: { appraisalName, periodName, employeeName, status },
    });
    let records = response.data.data || [];

    if (appraisalName) {
      records = records.filter((record) =>
        record.name?.toLowerCase().includes(appraisalName.toLowerCase())
      );
    }
    if (periodName) {
      records = records.filter((record) =>
        record.period_id?.name.toLowerCase().includes(periodName.toLowerCase())
      );
    }
    if (employeeName) {
      records = records.filter((record) =>
        record.employee_id?.full_name
          .toLowerCase()
          .includes(employeeName.toLowerCase())
      );
    }
    if (status) {
      records = records.filter((record) => record.status === status);
    }

    setLoading(false);
    return records;
  } catch (error) {
    setLoading(false);
    Swal.fire({
      icon: "error",
      title: "Error",
      text:
        error.response?.data?.message || "Failed to search team appraisals.",
    });
    return [];
  }
};

export const getAppraisalSummary = async (userId) => {
  try {
    const response = await axios.get(`/appraisal/summary/${userId}`);
    return response.data.data;
  } catch (error) {
    console.error("Error fetching appraisal summary:", error);
    return null;
  }
};

export const searchAppraisalSummary = async ({
  userId,
  appraisalName,
  periodName,
  employeeName,
  epf_number,
  status,
  setLoading,
}) => {
  try {
    setLoading(true);
    const response = await axios.get(`/appraisal/summary/${userId}`, {
      params: { appraisalName, periodName, employeeName, epf_number, status },
    });
    let records = response.data.data || [];

    if (appraisalName) {
      records = records.filter((record) =>
        record.name?.toLowerCase().includes(appraisalName.toLowerCase())
      );
    }
    if (periodName) {
      records = records.filter((record) =>
        record.period?.toLowerCase().includes(periodName.toLowerCase())
      );
    }
    if (employeeName) {
      records = records.filter((record) =>
        record.employee?.toLowerCase().includes(employeeName.toLowerCase())
      );
    }
    if (epf_number) {
      records = records.filter((record) =>
        record.epf_number?.toLowerCase().includes(epf_number.toLowerCase())
      );
    }
    if (status) {
      records = records.filter((record) => record.status === status);
    }

    return records;
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text:
        error.response?.data?.message || "Failed to search appraisal summary.",
    });
    return [];
  } finally {
    setLoading(false);
  }
};

export const getBellCurveData = async (userId, params = {}) => {
  try {
    const response = await axios.get(
      `/appraisal/rating-distribution/${userId}`,
      { params }
    );
    return response.data.data;
  } catch (error) {
    console.error("Error fetching bell curve data:", error);
    throw error.response?.data?.message || "Failed to fetch bell curve data";
  }
};
